import {
  SP_RATINGS,
  MOODYS_RATINGS,
  FITCH_RATINGS,
  JCR_RATINGS,
  type SpRating,
  type MoodysRating,
  type FitchRating,
  type JcrRating
} from '@klear/constants'

export type IgRatingFields = {
  spRating?: SpRating | string | null
  moodysRating?: MoodysRating | string | null
  fitchRating?: FitchRating | string | null
  jcrRating?: JcrRating | string | null
}

export const MOODYS_TO_SP: Record<MoodysRating, SpRating> = {
  Aaa: 'AAA',
  Aa1: 'AA+',
  Aa2: 'AA',
  Aa3: 'AA-',
  A1: 'A+',
  A2: 'A',
  A3: 'A-',
  Baa1: 'BBB+',
  Baa2: 'BBB',
  Baa3: 'BBB-'
}

export const FITCH_TO_SP: Record<FitchRating, SpRating> = {
  AAA: 'AAA',
  'AA+': 'AA+',
  AA: 'AA',
  'AA-': 'AA-',
  'A+': 'A+',
  A: 'A',
  'A-': 'A-',
  'BBB+': 'BBB+',
  BBB: 'BBB',
  'BBB-': 'BBB-'
}

export const JCR_TO_SP: Record<JcrRating, SpRating> = {
  AAA: 'AAA',
  'AA+': 'AA+',
  AA: 'AA',
  'AA-': 'AA-',
  'A+': 'A+',
  A: 'A',
  'A-': 'A-',
  'BBB+': 'BBB+',
  BBB: 'BBB',
  'BBB-': 'BBB-'
}

export function getRatingRank(rating: SpRating): number {
  return SP_RATINGS.indexOf(rating)
}

export function normalizeMoodysToSp(moodysRating: MoodysRating): SpRating {
  return MOODYS_TO_SP[moodysRating]
}

export function normalizeFitchToSp(fitchRating: FitchRating): SpRating {
  return FITCH_TO_SP[fitchRating]
}

export function normalizeJcrToSp(jcrRating: JcrRating): SpRating {
  return JCR_TO_SP[jcrRating]
}

export type RatingAgency = 'S&P' | "Moody's" | 'Fitch' | 'JCR'

export interface RatingWithAgency {
  rating: string
  agency: RatingAgency
}

export function getHighestIgRatingWithAgency({
  spRating,
  moodysRating,
  fitchRating,
  jcrRating
}: IgRatingFields = {}): RatingWithAgency | null {
  const candidates: Array<{ original: string; normalized: SpRating; agency: RatingAgency }> = []

  if (spRating && SP_RATINGS.includes(spRating as SpRating)) {
    candidates.push({
      original: spRating,
      normalized: spRating as SpRating,
      agency: 'S&P'
    })
  }

  if (moodysRating && MOODYS_RATINGS.includes(moodysRating as MoodysRating)) {
    candidates.push({
      original: moodysRating,
      normalized: normalizeMoodysToSp(moodysRating as MoodysRating),
      agency: "Moody's"
    })
  }

  if (fitchRating && FITCH_RATINGS.includes(fitchRating as FitchRating)) {
    candidates.push({
      original: fitchRating,
      normalized: normalizeFitchToSp(fitchRating as FitchRating),
      agency: 'Fitch'
    })
  }

  if (jcrRating && JCR_RATINGS.includes(jcrRating as JcrRating)) {
    candidates.push({
      original: jcrRating,
      normalized: normalizeJcrToSp(jcrRating as JcrRating),
      agency: 'JCR'
    })
  }

  if (candidates.length === 0) {
    return null
  }

  const best = candidates.reduce((bestCandidate, current) => {
    return getRatingRank(current.normalized) < getRatingRank(bestCandidate.normalized) ? current : bestCandidate
  })

  return {
    rating: best.original,
    agency: best.agency
  }
}
